/**
 * PublishToLessonModal.jsx
 * ────────────────────────
 * Beautiful Deskey-themed publish picker.
 *  • Shows existing Chapters (lessons) for a class.
 *  • Lets the teacher create a new Chapter inline.
 *  • Asks for a Topic headline + date for today's publish.
 *
 * Calls POST /studio/drafts/{draft_id}/publish-to-lesson with
 *   { class_id, lesson_id | lesson_title, topic_title, topic_date }.
 */
import React, { useEffect, useState } from 'react';
import { X, BookOpen, Check, Loader2 } from 'lucide-react';
import { PRIMARY_BTN, SECONDARY_BTN, CARD, INPUT, FIELD_LABEL, choiceClass } from './teacher/teacherUi';
import { toast } from 'sonner';
import AssignmentBlock from './AssignmentBlock';
import { notifyError } from '../lib/errors';
import Loader from './Loader';

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token') || '';
  const res = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  const t = await res.text();
  try { return JSON.parse(t); } catch { return { detail: t }; }
};

export default function PublishToLessonModal({ open, onClose, draft, className, classes = [], onPublished }) {
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [mode, setMode] = useState('pick'); // 'pick' | 'new'
  const [topicTitle, setTopicTitle] = useState('');
  const [topicDate, setTopicDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  // The class this session files into. Defaults to the class chosen on the
  // session card (draft.class_id), but stays editable here — the teacher can
  // switch to any other assigned class, which refreshes the chapter list.
  const [selectedClassId, setSelectedClassId] = useState(null);
  // Assigned classes flattened from the dept › batch › class tree, so each
  // option carries its batch name — same context the session cards show.
  const [fetchedClasses, setFetchedClasses] = useState([]);
  const propClassOptions = (classes || [])
    .map((c) => ({ id: c?.id || c?.class_id, name: c?.name || c?.class_name || c?.subject || 'Class' }))
    .filter((c) => c.id);
  // Prefer the grouped source (carries batch context); fall back to the flat prop.
  const classOptions = fetchedClasses.length ? fetchedClasses : propClassOptions;

  // ── Optional assignment that gets attached after the topic publishes ──
  const [asgnEnabled, setAsgnEnabled] = useState(false);
  const [asgnForm, setAsgnForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '23:59',
    points: 0,
  });

  // Reset the modal each time it opens.
  useEffect(() => {
    if (!open) return;
    setTopicTitle(draft?.title || 'Today\'s topic');
    // An update keeps the topic on its original day (it must not jump to
    // today); a first publish defaults to today.
    setTopicDate((draft?.published_topic_date || new Date().toISOString()).slice(0, 10));
    setSelectedLessonId(null);
    setNewChapterTitle('');
    setSelectedClassId(draft?.class_id || null);
    setFetchedClasses([]);
    // Reset assignment slot each open. Default due date = +7 days.
    setAsgnEnabled(false);
    const inOneWeek = new Date(); inOneWeek.setDate(inOneWeek.getDate() + 7);
    setAsgnForm({
      title: '', description: '', dueDate: inOneWeek.toISOString().slice(0, 10),
      dueTime: '23:59', points: 0,
    });
  }, [open, draft?.class_id, draft?.title, draft?.published_topic_date]);

  // Load the teacher's assigned classes grouped by dept › batch, so the picker
  // can show each class with its batch (matching the session cards) and the
  // teacher can switch class regardless of what the draft came in with.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const r = await api('/teacher/classes/grouped');
      if (!alive) return;
      const out = [];
      for (const g of r?.groups || []) {
        for (const b of g.batches || []) {
          for (const c of b.classes || []) {
            out.push({
              id: c.id,
              name: c.name,
              context: [b.batch?.name, g.department?.name].filter(Boolean).join(' · '),
            });
          }
        }
      }
      setFetchedClasses(out);
    })();
    return () => { alive = false; };
  }, [open]);

  // Load chapters whenever the target class changes (inherited or picked here).
  useEffect(() => {
    if (!open) return;
    if (!selectedClassId) { setChapters([]); setSelectedLessonId(null); setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api(`/teacher/classes/${selectedClassId}/lessons`);
      if (!alive) return;
      const list = r.lessons || [];
      setChapters(list);
      if (list.length > 0) {
        setMode('pick');
        // Default to the chapter the teacher chose on the session card, so the
        // topic files into the right chapter; fall back to the newest chapter.
        const preselect = (draft?.lesson_id && list.some((c) => c.id === draft.lesson_id))
          ? draft.lesson_id
          : list[0].id;
        setSelectedLessonId(preselect);
      } else {
        setMode('new');
        setSelectedLessonId(null);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, selectedClassId, draft?.lesson_id]);

  if (!open) return null;

  const selectedClassName = classOptions.find((c) => c.id === selectedClassId)?.name
    || className || draft?.detected_class_name || 'class';

  const canSubmit = (() => {
    if (!selectedClassId) return false;
    if (!topicTitle.trim()) return false;
    if (mode === 'pick' && !selectedLessonId) return false;
    if (mode === 'new' && !newChapterTitle.trim()) return false;
    // If assignment is enabled, require title + due date
    if (asgnEnabled && (!asgnForm.title.trim() || !asgnForm.dueDate)) return false;
    return true;
  })();

  const handlePublish = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    const body = {
      class_id: selectedClassId,
      topic_title: topicTitle.trim(),
      topic_date: topicDate,
    };
    if (mode === 'pick') body.lesson_id = selectedLessonId;
    else body.lesson_title = newChapterTitle.trim();

    try {
      const res = await api(`/teacher/studio/drafts/${draft.id}/publish-to-lesson`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.published) {
        // Attach an assignment if the teacher opted in
        if (asgnEnabled && res.topic_id) {
          try {
            const aRes = await api('/teacher/assignments', {
              method: 'POST',
              body: JSON.stringify({
                topic_id: res.topic_id,
                title: asgnForm.title.trim(),
                description: asgnForm.description.trim(),
                due_date: asgnForm.dueDate,
                due_time: asgnForm.dueTime,
                points: asgnForm.points,
              }),
            });
            if (aRes?.assignment) {
              toast.success(`Published + assignment "${asgnForm.title}" attached`);
            } else {
              toast.success(`Published "${res.topic_title}" to ${res.class_name}`);
              notifyError(aRes, 'Topic published, but assignment failed to save.');
            }
          } catch {
            toast.success(`Published "${res.topic_title}" to ${res.class_name}`);
            toast.error('Topic published, but assignment failed to save.');
          }
        } else if (res.unchanged) {
          // Server skipped it: nothing differs from what students already have.
          toast.info(res.message);
        } else {
          toast.success(`Published "${res.topic_title}" to ${res.class_name}`);
        }
        onPublished && onPublished(res);
        onClose();
      } else {
        notifyError(res, 'Publish failed');
      }
    } catch (e) {
      toast.error('Network error — please retry');
    }
    setSubmitting(false);
  };

  const selectedChapterTitle = chapters.find(c => c.id === selectedLessonId)?.title || 'selected chapter';
  const EMPTY_NOTE = 'py-6 text-center text-sm text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200';
  const segClass = (on) => `h-8 px-3 rounded-md text-sm font-medium transition-colors ${on ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'} disabled:opacity-40`;
  return (
    <div
      className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="publish-to-lesson-modal"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-slate-900">Publish to Student Desk</h2>
            <p className="mt-1 text-sm text-slate-600">File today's topic under a chapter in {selectedClassName}. Students see it with today's date</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 flex-shrink-0" data-testid="publish-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Class picker — defaults to the class chosen on the session card; switching refreshes the chapters. */}
          <section>
            <p className="text-sm font-medium text-slate-900 mb-3">Class</p>
            {classOptions.length === 0 ? (
              <div className={EMPTY_NOTE}>No classes assigned yet</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="publish-class-picker">
                {classOptions.map((c) => {
                  const active = selectedClassId === c.id;
                  return (
                    <button key={c.id} onClick={() => setSelectedClassId(c.id)} aria-pressed={active}
                      className={`${choiceClass(active)} px-4 py-2.5`}
                      data-testid={`publish-pick-class-${c.id}`}>
                      <span className="block text-sm font-medium text-slate-900">{c.name}</span>
                      {c.context && <span className="block text-sm text-slate-500">{c.context}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Chapter picker */}
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-sm font-medium text-slate-900">Chapter</p>
              <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
                <button onClick={() => setMode('pick')} disabled={chapters.length === 0} className={segClass(mode === 'pick')} data-testid="publish-mode-pick">Pick existing</button>
                <button onClick={() => setMode('new')} className={segClass(mode === 'new')} data-testid="publish-mode-new">New chapter</button>
              </div>
            </div>

            {/* Always say which chapter this files into, so the one picked on the session card is obvious. */}
            {mode === 'pick' && selectedLessonId && (
              <p className="mb-3 text-sm text-slate-600 flex items-center gap-2" data-testid="publish-selected-chapter">
                <Check className="w-4 h-4 text-[#1A7F37] flex-shrink-0" />
                <span className="min-w-0">
                  Filing under <span className="font-medium text-slate-900">{selectedChapterTitle}</span>
                  {draft?.lesson_id && draft.lesson_id === selectedLessonId && <span className="text-slate-500"> · picked on the session card</span>}
                </span>
              </p>
            )}

            {!selectedClassId ? (
              <div className={EMPTY_NOTE}>Choose a class above to see its chapters</div>
            ) : mode === 'pick' ? (
              loading ? (
                <Loader label="Loading chapters…" />
              ) : chapters.length === 0 ? (
                <div className={EMPTY_NOTE}>No chapters yet. Switch to New chapter to add one</div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {chapters.map(ch => {
                    const active = selectedLessonId === ch.id;
                    const topics = ch.topic_count || 0;
                    return (
                      <button key={ch.id} onClick={() => setSelectedLessonId(ch.id)} aria-pressed={active}
                        className={`${choiceClass(active)} w-full flex items-center gap-3 p-3`}
                        data-testid={`publish-pick-chapter-${ch.id}`}>
                        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                          {active ? <Check className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-slate-900 truncate">{ch.title}</span>
                          <span className="block text-sm text-slate-500 tabular-nums">
                            {topics} {topics === 1 ? 'topic' : 'topics'}
                            {ch.latest_topic_date && ` · last on ${new Date(ch.latest_topic_date).toLocaleDateString()}`}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <label className={FIELD_LABEL} htmlFor="publish-new-chapter">New chapter name</label>
                <input id="publish-new-chapter" value={newChapterTitle} onChange={(e) => setNewChapterTitle(e.target.value)}
                  placeholder="e.g. Chapter 4, Thermodynamics" className={INPUT} data-testid="publish-new-chapter-title" autoFocus />
                <p className="text-sm text-slate-500 mt-2">Chapters group your daily topics. You can rename or reorder them later</p>
              </div>
            )}
          </section>

          {/* Topic details */}
          <section className={`${CARD} p-4 space-y-4`}>
            <p className="text-sm font-medium text-slate-900">Today's topic</p>
            <div>
              <label className={FIELD_LABEL} htmlFor="publish-topic-title">Topic headline</label>
              <input id="publish-topic-title" value={topicTitle} onChange={(e) => setTopicTitle(e.target.value)}
                placeholder="What did you cover today?" className={INPUT} data-testid="publish-topic-title" />
            </div>
            <div>
              <label className={FIELD_LABEL} htmlFor="publish-topic-date">Date</label>
              <input id="publish-topic-date" type="date" value={topicDate} onChange={(e) => setTopicDate(e.target.value)}
                className={`${INPUT} !w-48`} data-testid="publish-topic-date" />
            </div>
          </section>

          {/* Optional assignment attachment */}
          <AssignmentBlock
            enabled={asgnEnabled}
            onToggle={setAsgnEnabled}
            title={asgnForm.title}
            description={asgnForm.description}
            dueDate={asgnForm.dueDate}
            dueTime={asgnForm.dueTime}
            points={asgnForm.points}
            onChange={(patch) => setAsgnForm(prev => ({ ...prev, ...patch }))}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className={SECONDARY_BTN} data-testid="publish-cancel">Cancel</button>
          <button onClick={handlePublish} disabled={!canSubmit || submitting} className={`${PRIMARY_BTN} flex items-center gap-2`} data-testid="publish-confirm">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</> : 'Publish topic'}
          </button>
        </div>
      </div>
    </div>
  );
}
